import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, ArrowRight, Package, Trash2 } from "lucide-react";

import { imageUrl } from "@/features/imageUpload";
import { storefrontPrice } from "@/features/storefront";
import { useStorefrontCardLookup } from "@/features/useStorefrontCardLookup";
import { useStorefrontBundleLookup } from "@/features/useStorefrontBundleLookup";
import { useShopCart } from "./shop";

export const Route = createFileRoute("/shop/cart")({
  component: ShopCart,
});

function ShopCart() {
  const cart = useShopCart();
  // TNK-124 phase G: resolve cart items via the lookup hook. Hits the
  // warm first page for the common case and falls back to single-card
  // fetches for cart items beyond it.
  const lookup = useStorefrontCardLookup(cart.ids);
  // TNK-212 phase 2b — resolve cart bundles alongside cards.
  const bundleLookup = useStorefrontBundleLookup(cart.productIds);
  const items = lookup.cards;
  const bundles = bundleLookup.bundles;
  const itemCount = items.length + bundles.length;
  const total =
    items.reduce((s, c) => s + storefrontPrice(c), 0) +
    bundles.reduce((s, b) => s + b.price, 0);
  const loading =
    lookup.phase === "loading" || bundleLookup.phase === "loading";
  const errored = lookup.phase === "error" || bundleLookup.phase === "error";

  if (cart.count === 0) {
    return (
      <div className="space-y-4 py-12 text-center">
        <p className="text-muted-foreground">Your reservation is empty.</p>
        <Link
          to="/shop/inventory"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          Browse cards
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="shop-display text-2xl font-bold text-foreground">
        Your reservation
      </h1>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : errored ? (
        <p className="text-destructive">Couldn’t load the inventory.</p>
      ) : (
        <>
          <ul className="divide-y divide-border rounded-md border border-border">
            {/* TNK-212 phase 2b — bundle rows first, then singles. */}
            {bundles.map((b) => {
              const img = b.coverRef ? imageUrl(b.coverRef, "thumb") : null;
              return (
                <li key={b.id} className="flex items-center gap-3 p-3">
                  <div className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-muted-foreground">
                    {img ? (
                      <img
                        src={img}
                        alt={b.name}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <Package size={16} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/shop/bundle/$id"
                      params={{ id: b.id }}
                      className="truncate text-sm font-medium text-foreground hover:underline"
                    >
                      {b.name}
                    </Link>
                    <div className="truncate text-[11px] text-muted-foreground">
                      Bundle · {b.cardCount} card{b.cardCount === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-foreground">
                    ${b.price.toFixed(2)}
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${b.name}`}
                    onClick={() => cart.removeProduct(b.id)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
            {items.map((card) => {
              // TNK-125: thumb — cart row image renders at 48×64.
              const img = card.imageFrontRef
                ? imageUrl(card.imageFrontRef, "thumb")
                : null;
              return (
                <li key={card.id} className="flex items-center gap-3 p-3">
                  <div className="h-16 w-12 shrink-0 overflow-hidden rounded bg-muted">
                    {img && (
                      <img
                        src={img}
                        alt={card.name}
                        className="h-full w-full object-contain"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/shop/card/$id"
                      params={{ id: card.id }}
                      className="truncate text-sm font-medium text-foreground hover:underline"
                    >
                      {card.name}
                    </Link>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {card.setName} · {card.condition}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-foreground">
                    ${storefrontPrice(card).toFixed(2)}
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${card.name}`}
                    onClick={() => cart.remove(card.id)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Cards removed from the store since they were added — sold,
              archived, or pulled. Listed by id with per-row + bulk
              remove so the buyer can clean the cart before reserving
              without bouncing back to the inventory grid. */}
          {lookup.missing.size > 0 && (
            <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/[0.04] p-3">
              <div className="flex items-start gap-2">
                <AlertCircle
                  size={14}
                  className="mt-0.5 shrink-0 text-amber-500"
                />
                <div className="flex-1">
                  <p className="text-[12px] font-semibold text-foreground">
                    {lookup.missing.size} card
                    {lookup.missing.size === 1 ? "" : "s"} no longer available
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Sold or removed since you added them. They&apos;ll be
                    skipped at reserve — remove now to tidy up.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    for (const id of lookup.missing) cart.remove(id);
                  }}
                  className="shrink-0 rounded-md border border-amber-500/40 px-2.5 py-1 text-[11px] font-semibold text-amber-500 hover:bg-amber-500/10"
                >
                  Remove all
                </button>
              </div>
              <ul className="space-y-1 pl-6">
                {[...lookup.missing].map((id) => (
                  <li
                    key={id}
                    className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground"
                  >
                    <span className="shop-mono truncate">{id}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${id}`}
                      onClick={() => cart.remove(id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="text-sm text-muted-foreground">
              {itemCount} item{itemCount !== 1 ? "s" : ""} ·{" "}
              <span
                className="shop-mono font-bold"
                style={{ color: "var(--color-primary)" }}
              >
                ${total.toFixed(2)}
              </span>
            </div>
            <Link
              to="/shop/reserve"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
